import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
import { useTranslation } from '@govard/i18n-core/react';
// Команда рабочего пространства: кто в нём состоит и кого позвали.
//
// До этого второй участник мог появиться только вставкой строки в базу, а
// статусы «отключён» / «удалён» модель знала, но выставить их было нечем.
import { useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { PlusOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../../shared/format/index.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { useCurrentScope, useCurrentUser } from '../../../shared/scope/useCurrentScope.js';
import type { MemberStatus, TeamInvitation, TeamMember } from '../api/team.client.js';
import {
  useCreateInvitationMutation,
  useRevokeInvitationMutation,
  useTeamInvitations,
  useTeamMembers,
  useUpdateMemberMutation,
} from '../hooks/useTeam.js';

const { Text, Paragraph } = Typography;

const STATUS_TONE: Record<MemberStatus, EntityStatusTone> = {
  active: 'success',
  suspended: 'warning',
  removed: 'default',
};

interface InviteFormValues {
  email: string;
  role: 'admin' | 'member' | 'viewer';
}

export function TeamPanel(): ReactElement {
  const { t } = useTranslation();
  const roleLabel = (role: string): string => t(`crm.team.role.${role}`);
  const memberStatusLabel = (status: MemberStatus): string => t(`crm.team.member_status.${status}`);
  const invitationStatusLabel = (status: TeamInvitation['status']): string =>
    t(`crm.team.invitation_status.${status}`);
  const assignableRoles = [
    { value: 'admin' as const, label: roleLabel('admin') },
    { value: 'member' as const, label: roleLabel('member') },
    { value: 'viewer' as const, label: roleLabel('viewer') },
  ];
  const scope = useCurrentScope();
  const currentUser = useCurrentUser();
  const membersQuery = useTeamMembers();
  const invitationsQuery = useTeamInvitations();
  const updateMut = useUpdateMemberMutation();
  const inviteMut = useCreateInvitationMutation();
  const revokeMut = useRevokeInvitationMutation();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [form] = Form.useForm<InviteFormValues>();

  // Можно ли приглашать, решает сервер: клиент не знает про особые пространства.
  const canInvite = invitationsQuery.data?.canInvite ?? false;
  const inviteBlockedReason = invitationsQuery.data?.reason ?? null;
  // Роли и статусы правит владелец или админ.
  const canManage = scope?.role === 'owner' || scope?.role === 'admin';
  const actionError = (updateMut.error ?? revokeMut.error) as Error | null;

  const memberColumns: ColumnsType<TeamMember> = [
    {
      title: t('crm.features.features.team.components.teampanel.участник'),
      key: 'name',
      render: (_, member) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {member.name || member.email}
            {member.userId === currentUser?.id ? (
              <Tag className="ml-2">
                {t('crm.features.features.team.components.teampanel.это_вы')}
              </Tag>
            ) : null}
          </span>
          <span className="text-xs text-gray-500">{member.email}</span>
        </div>
      ),
    },
    {
      title: t('crm.features.features.team.components.teampanel.роль'),
      key: 'role',
      width: 200,
      render: (_, member) => {
        const isSelf = member.userId === currentUser?.id;
        // Владельца понижают только передачей владения, свою роль не меняют.
        if (!canManage || member.role === 'owner' || isSelf) {
          return <Tag>{roleLabel(member.role)}</Tag>;
        }
        return (
          <Select<'admin' | 'member' | 'viewer'>
            size="small"
            style={{ width: 160 }}
            value={member.role as 'admin' | 'member' | 'viewer'}
            options={assignableRoles}
            loading={updateMut.isPending && updateMut.variables?.id === member.id}
            onChange={(role) => void updateMut.mutateAsync({ id: member.id, input: { role } })}
          />
        );
      },
    },
    {
      title: t('crm.features.features.team.components.teampanel.статус'),
      key: 'status',
      width: 130,
      render: (_, member) => (
        <EntityStatusChip tone={STATUS_TONE[member.status]}>
          {memberStatusLabel(member.status)}
        </EntityStatusChip>
      ),
    },
    {
      title: t('crm.features.features.team.components.teampanel.действия'),
      key: 'actions',
      width: 140,
      render: (_, member) => {
        if (!canManage || member.userId === currentUser?.id) {
          return <Text type="secondary">—</Text>;
        }
        if (member.status === 'active') {
          return (
            <DeleteEntityButton
              label={t('crm.features.features.team.components.teampanel.отключить')}
              okText={t('crm.features.features.team.components.teampanel.отключить_2')}
              title={t('crm.features.features.team.components.teampanel.отключить_участника')}
              description={t(
                'crm.features.features.team.components.teampanel.человек_перестанет_попадать_в_пространство',
              )}
              loading={updateMut.isPending && updateMut.variables?.id === member.id}
              onConfirm={() =>
                updateMut.mutateAsync({ id: member.id, input: { status: 'suspended' } })
              }
            />
          );
        }
        return (
          <Button
            size="small"
            loading={updateMut.isPending && updateMut.variables?.id === member.id}
            onClick={() =>
              void updateMut.mutateAsync({ id: member.id, input: { status: 'active' } })
            }
          >
            {t('crm.features.features.team.components.teampanel.вернуть')}
          </Button>
        );
      },
    },
  ];

  const invitationColumns: ColumnsType<TeamInvitation> = [
    {
      title: t('crm.features.features.team.components.teampanel.адрес'),
      key: 'email',
      render: (_, invitation) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{invitation.email}</span>
          <span className="text-xs text-gray-500">
            {roleLabel(invitation.role)}
            {invitation.invitedByName
              ? ` · ${t('crm.team.invited_by', { name: invitation.invitedByName })}`
              : ''}
          </span>
        </div>
      ),
    },
    {
      title: t('crm.features.features.team.components.teampanel.статус_2'),
      key: 'status',
      width: 150,
      render: (_, invitation) => {
        const expired =
          invitation.status === 'pending' && new Date(invitation.expiresAt) <= new Date();
        return (
          <EntityStatusChip
            tone={expired ? 'default' : invitation.status === 'pending' ? 'processing' : 'default'}
          >
            {expired
              ? t('crm.features.features.team.components.teampanel.просрочено')
              : invitationStatusLabel(invitation.status)}
          </EntityStatusChip>
        );
      },
    },
    {
      title: t('crm.features.features.team.components.teampanel.действует_до'),
      key: 'expiresAt',
      width: 180,
      render: (_, invitation) => (
        <span className="text-sm text-gray-600">{formatDateTime(invitation.expiresAt)}</span>
      ),
    },
    {
      title: t('crm.features.features.team.components.teampanel.действия_2'),
      key: 'actions',
      width: 120,
      render: (_, invitation) =>
        canInvite && invitation.status === 'pending' ? (
          <DeleteEntityButton
            label={t('crm.features.features.team.components.teampanel.отозвать')}
            okText={t('crm.features.features.team.components.teampanel.отозвать_2')}
            title={t('crm.features.features.team.components.teampanel.отозвать_приглашение')}
            description={t(
              'crm.features.features.team.components.teampanel.ссылка_перестанет_работать',
            )}
            loading={revokeMut.isPending && revokeMut.variables === invitation.id}
            onConfirm={() => revokeMut.mutateAsync(invitation.id)}
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  async function handleInvite(values: InviteFormValues): Promise<void> {
    const result = await inviteMut.mutateAsync(values);
    // Ссылку показываем сразу: письмо может не дойти, а почта может быть не
    // настроена — тогда её передают лично.
    setLastInviteUrl(result.acceptUrl);
    form.resetFields();
    setInviteOpen(false);
  }

  const members = membersQuery.data ?? [];
  const invitations = invitationsQuery.data?.invitations ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {t('crm.features.features.team.components.teampanel.команда')}
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t(
              'crm.features.features.team.components.teampanel.кто_состоит_в_пространстве_и_кого_позвали_',
            )}
          </Text>
        </div>
        {canInvite ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ email: '', role: 'member' });
              setInviteOpen(true);
            }}
          >
            {t('crm.features.features.team.components.teampanel.пригласить')}
          </Button>
        ) : null}
      </div>

      {inviteBlockedReason ? (
        <Alert
          type="info"
          showIcon
          title={t('crm.features.features.team.components.teampanel.приглашения_недоступны')}
          description={inviteBlockedReason}
        />
      ) : null}

      {actionError ? (
        <Alert
          type="error"
          showIcon
          closable
          title={t('crm.features.features.team.components.teampanel.не_получилось')}
          description={actionError.message}
        />
      ) : null}

      {lastInviteUrl ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setLastInviteUrl(null)}
          title={t('crm.features.features.team.components.teampanel.приглашение_создано')}
          description={
            <div className="flex flex-col gap-1">
              <span>
                {t(
                  'crm.features.features.team.components.teampanel.письмо_поставлено_в_очередь_ссылку_можно_п',
                )}
              </span>
              <Paragraph copyable={{ text: lastInviteUrl }} style={{ margin: 0, fontSize: 12 }}>
                {lastInviteUrl}
              </Paragraph>
            </div>
          }
        />
      ) : null}

      <section className="flex flex-col gap-2">
        {membersQuery.isError ? (
          <ErrorState
            title={t(
              'crm.features.features.team.components.teampanel.не_удалось_загрузить_участников',
            )}
            error={membersQuery.error}
            onRetry={() => void membersQuery.refetch()}
          />
        ) : membersQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spin />
          </div>
        ) : (
          <Table<TeamMember>
            showSorterTooltip={TABLE_SORTER_TOOLTIP}
            size="small"
            rowKey="id"
            dataSource={members}
            columns={memberColumns}
            pagination={false}
          />
        )}
      </section>

      {canInvite ? (
        <section className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-gray-900">
            {t('crm.features.features.team.components.teampanel.приглашения')}
          </div>
          {invitationsQuery.isError ? (
            <ErrorState
              title={t(
                'crm.features.features.team.components.teampanel.не_удалось_загрузить_приглашения',
              )}
              error={invitationsQuery.error}
              onRetry={() => void invitationsQuery.refetch()}
            />
          ) : invitations.length === 0 ? (
            <Empty
              description={t(
                'crm.features.features.team.components.teampanel.никого_не_приглашали',
              )}
            />
          ) : (
            <Table<TeamInvitation>
              showSorterTooltip={TABLE_SORTER_TOOLTIP}
              size="small"
              rowKey="id"
              dataSource={invitations}
              columns={invitationColumns}
              pagination={false}
            />
          )}
        </section>
      ) : null}

      <Modal
        open={inviteOpen}
        title={t('crm.features.features.team.components.teampanel.пригласить_в_пространство')}
        okText={t('crm.features.features.team.components.teampanel.отправить_приглашение')}
        cancelText={t('crm.features.features.team.components.teampanel.отмена')}
        confirmLoading={inviteMut.isPending}
        onOk={() => form.submit()}
        onCancel={() => setInviteOpen(false)}
        destroyOnHidden
      >
        {inviteMut.error ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            title={t('crm.features.features.team.components.teampanel.не_удалось_пригласить')}
            description={(inviteMut.error as Error).message}
          />
        ) : null}
        <Form<InviteFormValues>
          form={form}
          layout="vertical"
          onFinish={(v) => void handleInvite(v)}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: t('crm.features.features.team.components.teampanel.укажите_адрес'),
              },
              {
                type: 'email',
                message: t(
                  'crm.features.features.team.components.teampanel.похоже_это_не_адрес_почты',
                ),
              },
            ]}
            extra={t(
              'crm.features.features.team.components.teampanel.принять_приглашение_можно_только_под_этим_',
            )}
          >
            <Input placeholder="colleague@example.com" maxLength={320} />
          </Form.Item>
          <Form.Item
            name="role"
            label={t('crm.features.features.team.components.teampanel.роль_2')}
            rules={[{ required: true }]}
          >
            <Select options={assignableRoles} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
